import { falBone } from 'shared/@generated/icons/falBone';
import { falBowlFood } from 'shared/@generated/icons/falBowlFood';
import { falBucket } from 'shared/@generated/icons/falBucket';
import { falGraduationCap } from 'shared/@generated/icons/falGraduationCap';
import { falHouseHeart } from 'shared/@generated/icons/falHouseHeart';
import { falScissors } from 'shared/@generated/icons/falScissors';
import { falShieldCheck } from 'shared/@generated/icons/falShieldCheck';
import { falSlidersSimple } from 'shared/@generated/icons/falSlidersSimple';
import { falStethoscope } from 'shared/@generated/icons/falStethoscope';
import { falTag } from 'shared/@generated/icons/falTag';
import DogYoung from 'shared/assets/doodles/dog-young.webp';
import DogAdult from 'shared/assets/doodles/dog-adult.webp';
import DogSenior from 'shared/assets/doodles/dog-senior.webp';
import CatYoung from 'shared/assets/doodles/cat-young.webp';
import CatAdult from 'shared/assets/doodles/cat-adult.webp';
import CatSenior from 'shared/assets/doodles/cat-senior.webp';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { Pressable, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Props } from 'shared/@types';
import type { Breed } from 'shared/@generated/models';
import type { BreedMarketData } from 'shared/@types/breed-market-data';
import { Text } from 'shared/components/text';
import { H1 } from 'shared/components/title';
import { Image } from 'shared/components/image';
import { FaIcon, Icon } from 'shared/components/icon';
import { Button } from 'shared/components/button';
import { MarkdownView } from 'shared/components/markdown-view';
import { PetInsurancePriceModal } from 'shared/components/pet-insurance-price-modal';
import { PetCostChart } from 'shared/components/pet-cost-chart';
import { PetCostBudgetModal } from 'shared/components/pet-cost-budget-modal';
import { Path, Svg } from 'shared/screens/breeds/breed-price-chart-svg';
import colors from 'shared/library/colors';
import useApi from 'shared/library/use-api';
import { apiRoute } from 'shared/library/api-route';
import { useLocale } from 'shared/library/i18n/use-locale';
import { usePartners } from 'shared/library/partners';
import { notify } from 'shared/library/notify';
import { useRoute } from 'shared/library/routes/route';
import { translateArray } from 'shared/library/i18n/get-translation-list';
import {
  PetCostAnimal,
  PetCostCategory,
  PetCostProfile,
  PetCostSettings,
  PetCostStage,
  defaultPetCostProfile,
  defaultPetCostSettings,
  petCostCategories,
  petCostColors,
  petCostProfileFromBreed,
  petCostYears,
  summarizePetCostStages,
} from 'shared/library/pet-costs';

interface PetCostsScreenProps extends Props {
  animal: PetCostAnimal;
}

interface SelectedBreed {
  breed: Breed;
  profile: PetCostProfile;
  marketStats: BreedMarketData | null;
}

const stageImages: Record<PetCostAnimal, Record<PetCostStage, string>> = {
  dog: { young: DogYoung, adult: DogAdult, senior: DogSenior },
  cat: { young: CatYoung, adult: CatAdult, senior: CatSenior },
};

export const petCostIcons: Record<PetCostCategory, FaIcon> = {
  purchase: falTag,
  food: falBowlFood,
  insurance: falShieldCheck,
  vet: falStethoscope,
  equipment: falBone,
  grooming: falScissors,
  litter: falBucket,
  care: falHouseHeart,
  training: falGraduationCap,
};

// Bottom-to-top order in the chart: steady costs first, the one-off purchase on top.
const chartOrder: PetCostCategory[] = [
  'food',
  'insurance',
  'vet',
  'litter',
  'grooming',
  'equipment',
  'care',
  'training',
  'purchase',
];

const numberFormatter = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });

const PetCostsScreen = ({ animal }: PetCostsScreenProps) => {
  const { t } = useTranslation('screens');
  const { countryCode, market } = useLocale();
  const route = useRoute();
  const { request } = useApi({ notifyOnServerErrors: false });
  const [selected, setSelected] = useState<SelectedBreed | null>(null);
  const [breedId, setBreedId] = useState<number | null>(null);
  const [loadingBreed, setLoadingBreed] = useState(false);
  const [stage, setStage] = useState<PetCostStage>('young');
  const profile = selected?.profile ?? defaultPetCostProfile(animal);
  const defaults = useMemo(
    () =>
      defaultPetCostSettings(
        animal,
        countryCode,
        selected?.marketStats?.median_price
          ? Math.round(selected.marketStats.median_price / 500) * 500
          : null,
      ),
    [animal, countryCode, selected?.marketStats?.median_price],
  );
  const [overrides, setOverrides] = useState<Partial<PetCostSettings>>({});
  const settings = { ...defaults, ...overrides };
  const years = petCostYears(animal, profile, settings, countryCode);
  const stages = summarizePetCostStages(years, profile.lifespanYears);
  const selectedStage = stages.find((summary) => summary.stage === stage)!;
  const requestId = useRef(0);

  const amount = (value: number) =>
    t('pet_costs.amount', { amount: numberFormatter.format(value), currency: market.currency });
  const breedName = selected?.breed.name;

  function clearBreed() {
    requestId.current++;
    setBreedId(null);
    setSelected(null);
    setLoadingBreed(false);
    setOverrides(({ purchasePrice: _purchasePrice, ...rest }) => rest);
  }

  function selectBreed(breed: Breed) {
    const id = ++requestId.current;

    setBreedId(breed.id);

    setLoadingBreed(true);

    Promise.all([
      request<{ breed: Breed }>(apiRoute('breeds.show', { breed: breed.slug.en })),
      request<{ market_stats: BreedMarketData | null }>(
        apiRoute('breeds.market-stats', { breed: breed.slug.en }),
      ).catch(() => ({ market_stats: null })),
    ])
      .then(([details, stats]) => {
        if (id !== requestId.current) return;

        // A new breed brings new defaults, so the purchase price follows the breed again.
        setOverrides(({ purchasePrice: _purchasePrice, ...rest }) => rest);
        setSelected({
          breed: details.breed,
          profile: petCostProfileFromBreed(animal, details.breed),
          marketStats: stats.market_stats,
        });
      })
      .catch((error) => {
        // Keep the previous breed and its numbers; the breed picker can simply be tried again.
        if (id !== requestId.current) return;

        setBreedId(selected?.breed.id ?? null);
        notify.error(error, 'pet-costs.tsx - selectBreed');
      })
      .finally(() => {
        if (id === requestId.current) setLoadingBreed(false);
      });
  }

  const sections = translateArray<{ title: string; text: string }>(
    t,
    `pet_costs.article.${animal}.sections`,
  );
  const sources = translateArray<{ title: string; url: string }>(
    t,
    `pet_costs.article.${animal}.sources`,
  );
  const otherAnimal: PetCostAnimal = animal === 'dog' ? 'cat' : 'dog';
  const articleMarkdown = [
    `## ${t(`pet_costs.article.${animal}.title`)}`,
    t(`pet_costs.article.${animal}.intro`),
    ...sections.map((section) => `### ${section.title}\n\n${section.text}`),
    `### ${t('pet_costs.more')}`,
    [
      `- [${t(`pet_costs.pages.${otherAnimal}.link_title`)}](${route(`pet-costs.${otherAnimal}`)}): ${t(`pet_costs.pages.${otherAnimal}.link_description`)}`,
      `- [${t(`pet_names.pages.${animal}.link_title`)}](${route(`pet-names.${animal}`)}): ${t(`pet_names.pages.${animal}.link_description`)}`,
    ].join('\n'),
    `### ${t('pet_costs.sources')}`,
    sources.map((source) => `- [${source.title}](${source.url})`).join('\n'),
  ].join('\n\n');

  return (
    <View className={'px-3 py-6 md:px-6 md:py-12'}>
      <View className={'mx-auto w-full max-w-screen-xl'}>
        <View className={'md:items-center'}>
          <H1
            title={t(`pet_costs.pages.${animal}.heading`)}
            className={'md:text-center'}
            marginBottom={false}
          />
          <Text
            className={
              'text-brand-brown-dark mt-1.5 max-w-prose text-pretty md:mt-3 md:text-center md:text-lg'
            }
          >
            {t(`pet_costs.pages.${animal}.intro`)}
          </Text>
        </View>

        <View className={clsx('mt-6 transition-opacity md:mt-10', loadingBreed && 'opacity-60')}>
          <View className={'flex-col-reverse gap-4 md:gap-5 lg:flex-row lg:items-stretch'}>
            <View
              className={
                'border-brand-brown-dark/15 flex-1 rounded border bg-white p-4 md:p-6 lg:mb-2'
              }
            >
              <View>
                <Text className={'text-brand-brown-dark text-lg font-black md:text-2xl'}>
                  {t(`pet_costs.chart_title.${animal}`)}
                </Text>
                <Text className={'text-brand-brown-dark/60 mt-1 text-sm md:text-base'}>
                  {t('pet_costs.chart_subtitle')}
                </Text>
              </View>

              <View className={'lg:flex-1'}>
                <View className={'mt-5 lg:flex-1'}>
                  <PetCostChart
                    years={years}
                    categories={chartOrder.filter((category) =>
                      petCostCategories[animal].includes(category),
                    )}
                    stages={stages}
                    selectedStage={selectedStage}
                    accessibilityLabel={t('pet_costs.chart_label', { years: years.length })}
                    formatAxis={(value) => numberFormatter.format(value)}
                    yearLabel={(year) => t('pet_costs.year', { year })}
                    stageLabel={(stage) => t(`pet_costs.stages.${animal}.${stage}`)}
                    onSelectStage={setStage}
                    className={'lg:h-auto lg:min-h-56 lg:flex-1'}
                  />
                </View>

                <View className={'mt-4 flex-row flex-wrap gap-x-4 gap-y-2'}>
                  {petCostCategories[animal]
                    .filter((category) => years.some((year) => (year[category] ?? 0) > 0))
                    .map((category) => (
                      <View key={category} className={'flex-row items-center gap-1.5'}>
                        <View
                          className={'size-3 rounded-full'}
                          style={{ backgroundColor: petCostColors[category] }}
                        />
                        <Text className={'text-brand-brown-dark/70 text-sm'}>
                          {t(`pet_costs.categories.${animal}.${category}`)}
                        </Text>
                      </View>
                    ))}
                </View>
              </View>
            </View>

            <Receipt
              animal={animal}
              summary={selectedStage}
              amount={amount}
              className={'lg:w-5/12'}
              header={
                <StageTabs animal={animal} stages={stages} value={stage} onChange={setStage} />
              }
              footer={
                <PetCostBudgetModal
                  animal={animal}
                  settings={settings}
                  breedId={breedId}
                  breedName={selected?.breed.name}
                  marketStats={selected?.marketStats ?? null}
                  onSelectBreed={selectBreed}
                  onClearBreed={clearBreed}
                  onChange={(changes) => setOverrides((current) => ({ ...current, ...changes }))}
                  onReset={() => setOverrides({})}
                  trigger={({ open }) => (
                    <View className={'mt-6 gap-3'}>
                      {breedName && (
                        <Text className={'text-brand-brown-dark/60 text-center text-sm'}>
                          {t('pet_costs.receipt.based_on', { name: breedName })}
                        </Text>
                      )}
                      <Button
                        text={t('pet_costs.customize')}
                        icon={falSlidersSimple}
                        iconPosition={'left'}
                        center={true}
                        onPress={open}
                        color={'brown'}
                        testID={'pet-costs-customize'}
                      />
                    </View>
                  )}
                />
              }
            />
          </View>
        </View>

        <MarkdownView className={'mt-12 md:mt-16'} markdown={articleMarkdown} />
      </View>
    </View>
  );
};

function Receipt({
  animal,
  summary,
  amount,
  className,
  header,
  footer,
}: {
  animal: PetCostAnimal;
  summary: ReturnType<typeof summarizePetCostStages>[number];
  amount: (value: number) => string;
  className?: string;
  header: ReactNode;
  footer: ReactNode;
}) {
  const { t } = useTranslation('screens');
  // The Trygg-Hansa price calculator only quotes Swedish customers.
  const showInsuranceCalculator = usePartners().insurancePartner.countryCode === 'SE';
  const rows = (Object.entries(summary.perYear) as [PetCostCategory, number][]).sort(
    (a, b) => b[1] - a[1],
  );
  const young = summary.stage === 'young';

  return (
    <View className={className}>
      <View
        className={
          'border-brand-brown-dark/15 rounded-t border border-b-0 bg-white px-4 pb-5 pt-4 md:px-6 md:pt-6'
        }
      >
        {header}

        <View className={'mt-6 gap-3'}>
          {rows.map(([category, value]) => (
            <View key={category} className={'flex-row items-center gap-3'}>
              <View
                className={'size-8 items-center justify-center rounded-full'}
                style={{ backgroundColor: petCostColors[category] }}
              >
                <Icon icon={petCostIcons[category]} className={'text-brand-brown-dark size-4'} />
              </View>
              <View className={'min-w-0 flex-1'}>
                <View className={'flex-row items-end gap-2'}>
                  <Text className={'text-brand-brown-dark shrink'}>
                    {t(`pet_costs.categories.${animal}.${category}`)}
                  </Text>
                  <View
                    className={
                      'border-brand-brown-dark/25 mb-1.5 min-w-4 flex-1 border-b border-dotted'
                    }
                  />
                  <Text
                    className={
                      'text-brand-brown-dark shrink-0 whitespace-nowrap font-bold tabular-nums'
                    }
                  >
                    {amount(value)}
                  </Text>
                </View>
                {category === 'insurance' && showInsuranceCalculator && (
                  <PetInsurancePriceModal
                    animal={animal}
                    trigger={({ open }) => (
                      <TouchableOpacity
                        accessibilityRole={'button'}
                        onPress={open}
                        testID={'pet-costs-insurance-price'}
                        className={'self-start'}
                      >
                        <Text className={'text-brand-green text-sm font-semibold underline'}>
                          {t('pet_costs.receipt.insurance_link')}
                        </Text>
                      </TouchableOpacity>
                    )}
                  />
                )}
              </View>
            </View>
          ))}
        </View>

        <View className={'mt-6'}>
          <View className={'flex-row items-baseline justify-between gap-2'}>
            <Text className={'text-brand-brown-dark font-bold'}>
              {young
                ? t('pet_costs.receipt.first_year_total')
                : t('pet_costs.receipt.per_year_total')}
            </Text>
            <Text
              testID={'pet-costs-receipt-total'}
              className={'text-brand-green text-3xl font-black tabular-nums'}
            >
              {amount(summary.perYearTotal)}
            </Text>
          </View>
          <Text className={'text-brand-brown-dark/60 mt-0.5 text-right'}>
            {t('pet_costs.per_month', {
              amount: amount(Math.round(summary.perYearTotal / 12 / 10) * 10),
            })}
          </Text>
        </View>

        {footer}
      </View>

      <ReceiptEdge />
    </View>
  );
}

// The only period selector: a segmented control at the top of the receipt.
function StageTabs({
  animal,
  stages,
  value,
  onChange,
}: {
  animal: PetCostAnimal;
  stages: ReturnType<typeof summarizePetCostStages>;
  value: PetCostStage;
  onChange: (stage: PetCostStage) => void;
}) {
  const { t } = useTranslation('screens');

  return (
    <View role={'radiogroup'} className={'bg-brand-cream flex-row gap-1 rounded p-1'}>
      {stages.map((summary) => {
        const active = summary.stage === value;

        return (
          <Pressable
            key={summary.stage}
            role={'radio'}
            aria-checked={active}
            accessibilityState={{ selected: active }}
            onPress={() => onChange(summary.stage)}
            testID={`pet-costs-stage-${summary.stage}`}
            className={clsx(
              'flex-1 items-center rounded px-1 pb-2.5 pt-2 transition-colors',
              active
                ? 'border-brand-brown-dark/15 border bg-white'
                : 'hover:bg-brand-cream-dark border border-transparent',
            )}
          >
            <Image
              src={stageImages[animal][summary.stage]}
              alt={''}
              imageClassName={'object-contain'}
              containerClassName={clsx(
                'h-14 w-full transition-opacity md:h-16',
                !active && 'opacity-50',
              )}
            />
            <Text
              className={clsx(
                'mt-1.5 font-bold',
                active ? 'text-brand-brown-dark' : 'text-brand-brown-dark/60',
              )}
            >
              {t(`pet_costs.stages.${animal}.${summary.stage}`)}
            </Text>
            <Text className={'text-brand-brown-dark/50 text-xs tabular-nums'}>
              {summary.firstYear === summary.lastYear
                ? t('pet_costs.year', { year: summary.firstYear })
                : t('pet_costs.year_range', { from: summary.firstYear, to: summary.lastYear })}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

// The receipt's zigzag bottom edge. Teeth count follows the measured width so every tooth stays even.
function ReceiptEdge() {
  const [width, setWidth] = useState(0);
  const depth = 7;
  const teeth = Math.max(1, Math.round(width / 14));
  const points = Array.from({ length: teeth }, (_, index) => {
    const start = (index * width) / teeth;
    const end = ((index + 1) * width) / teeth;

    return `L${(start + end) / 2},${depth} L${end},0.5`;
  }).join(' ');

  return (
    <View
      aria-hidden={true}
      className={'h-2'}
      onLayout={(event) => setWidth(event.nativeEvent.layout.width)}
    >
      {width > 0 && (
        <Svg width={width} height={depth + 1}>
          <Path d={`M0,0 L0,0.5 ${points} L${width},0 Z`} fill={'#ffffff'} />
          <Path
            d={`M0,0.5 ${points}`}
            fill={'none'}
            stroke={colors.brand['brown-dark']}
            strokeOpacity={0.15}
            strokeWidth={1}
            strokeLinejoin={'round'}
          />
        </Svg>
      )}
    </View>
  );
}

export { PetCostsScreen };
export type { PetCostsScreenProps };
